import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, Megaphone, Wrench, Lightbulb, Sparkles, ChevronRight, CheckCheck, MessageCircle, CheckCircle, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const UPDATE_ICONS = {
  announcement: Megaphone,
  system_update: Wrench,
  exam_tips: Lightbulb,
  new_feature: Sparkles
};

const FORUM_ICONS = {
  forum_answer: MessageCircle,
  forum_verified: CheckCircle,
  forum_follow_up: MessageSquare
};

const PRIORITY_COLORS = {
  urgent: 'text-red-600',
  important: 'text-yellow-600',
  normal: 'text-gray-600'
};

const LAST_READ_KEY = 'notifications_last_read';

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [forumNotifications, setForumNotifications] = useState([]);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [systemUnreadCount, setSystemUnreadCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [forumUnreadCount, setForumUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [lastReadTimestamp, setLastReadTimestamp] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'updates', 'forum'

  // Get last read timestamp - check localStorage first, then user profile
  const getLastReadTime = useCallback(() => {
    if (!user?.indos_number) return null;
    
    // Check localStorage first (for fast access)
    const key = `${LAST_READ_KEY}_${user.indos_number}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      const timestamp = parseInt(stored, 10);
      if (!isNaN(timestamp)) {
        return timestamp;
      }
    }
    
    // Fallback to user profile (survives cache clears)
    if (user.notifications_last_read_at) {
      // Also cache it in localStorage for next time
      localStorage.setItem(key, user.notifications_last_read_at.toString());
      return user.notifications_last_read_at;
    }
    
    return null;
  }, [user?.indos_number, user?.notifications_last_read_at]);

  // Initialize lastReadTimestamp on mount and when user changes
  useEffect(() => {
    if (user) {
      const timestamp = getLastReadTime();
      setLastReadTimestamp(timestamp);
    }
  }, [user, getLastReadTime]);

  // Mark all as read - save to both localStorage AND backend
  const markAllAsRead = useCallback(async () => {
    if (!user?.indos_number) return;
    
    const now = Date.now(); // Unix timestamp in milliseconds
    const key = `${LAST_READ_KEY}_${user.indos_number}`;
    
    // Immediately update localStorage and state for instant UI feedback
    localStorage.setItem(key, now.toString());
    setLastReadTimestamp(now);
    setUnreadCount(0);
    setForumUnreadCount(0);
    setSystemUnreadCount(0);
    
    // Save to backend (for persistence across cache clears/devices)
    try {
      await Promise.all([
        axios.post(`${API}/updates/mark-all-read`),
        axios.post(`${API}/forum/notifications/mark-all-read`),
        axios.post(`${API}/notifications/mark-all-read`).catch(() => null),
      ]);
    } catch (error) {
      console.error('Failed to save read status to backend:', error);
      // localStorage already updated, so UI still works
    }
  }, [user?.indos_number]);

  const fetchRecentUpdates = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Fetch updates, forum notifications, and system notifications in parallel
      const [updatesRes, forumRes, sysRes] = await Promise.all([
        axios.get(`${API}/updates/my-updates/recent?limit=5`).catch(() => ({ data: { updates: [] } })),
        axios.get(`${API}/forum/notifications?page_size=5`).catch(() => ({ data: { notifications: [], unread_count: 0 } })),
        axios.get(`${API}/notifications?limit=5`).catch(() => ({ data: { notifications: [], unread_count: 0 } })),
      ]);

      const fetchedUpdates = updatesRes.data.updates || [];
      const fetchedForumNotifs = forumRes.data.notifications || [];
      const forumUnread = forumRes.data.unread_count || 0;
      const fetchedSysNotifs = sysRes.data.notifications || [];
      const sysUnread = sysRes.data.unread_count || 0;

      setUpdates(fetchedUpdates);
      setForumNotifications(fetchedForumNotifs);
      setForumUnreadCount(forumUnread);
      setSystemNotifications(fetchedSysNotifs);
      setSystemUnreadCount(sysUnread);
      
      // Calculate unread count for updates using Unix timestamp comparison
      const lastRead = lastReadTimestamp || getLastReadTime();
      let updatesUnread = 0;
      if (lastRead) {
        updatesUnread = fetchedUpdates.filter(u => {
          const updateTime = new Date(u.created_at).getTime();
          return updateTime > lastRead;
        }).length;
      } else {
        updatesUnread = fetchedUpdates.length;
      }
      setUnreadCount(updatesUnread);
    } catch (error) {
      console.error('Failed to fetch updates:', error);
    } finally {
      setLoading(false);
    }
  }, [user, lastReadTimestamp, getLastReadTime]);

  // Fetch on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchRecentUpdates();
    }
  }, [user, fetchRecentUpdates]);

  // Re-fetch when dropdown opens to catch new updates
  useEffect(() => {
    if (isOpen && user) {
      fetchRecentUpdates();
    }
  }, [isOpen, user, fetchRecentUpdates]);

  const getIcon = (type) => {
    const Icon = UPDATE_ICONS[type] || FORUM_ICONS[type] || Bell;
    return Icon;
  };

  const getForumNotificationMessage = (notif) => {
    switch (notif.type) {
      case 'forum_answer':
        return `${notif.actor_name} answered your question`;
      case 'forum_verified':
        return `Your answer was verified by ${notif.actor_name}`;
      case 'forum_follow_up':
        return `${notif.actor_name} added a follow-up`;
      default:
        return 'New forum activity';
    }
  };

  // Check if an update is unread using Unix timestamp comparison
  const isUnread = (update) => {
    const lastRead = lastReadTimestamp || getLastReadTime();
    if (!lastRead) return true;
    const updateTime = new Date(update.created_at).getTime();
    return updateTime > lastRead;
  };

  // Don't show if not logged in
  if (!user) {
    return null;
  }

  const totalUnread = unreadCount + forumUnreadCount + systemUnreadCount;
  const allNotifications = [
    ...updates.map(u => ({ ...u, source: 'update' })),
    ...forumNotifications.map(n => ({ ...n, source: 'forum' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 8);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative text-gray-600 hover:text-[#0A7EA4]"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
              {totalUnread > 9 ? '9+' : totalUnread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="px-3 py-2 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <p className="text-xs text-gray-500">
              {totalUnread > 0 ? `${totalUnread} new` : 'No new notifications'}
            </p>
          </div>
          {totalUnread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                markAllAsRead();
              }}
              className="text-xs text-[#0A7EA4] hover:text-[#0A7EA4]/80 h-7 px-2"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Tab switcher */}
        <div className="flex border-b px-1 pt-1">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-t transition-colors ${
              activeTab === 'all' 
                ? 'bg-gray-100 text-gray-900' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('updates')}
            className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-t transition-colors ${
              activeTab === 'updates' 
                ? 'bg-gray-100 text-gray-900' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Updates {unreadCount > 0 && <span className="ml-1 text-blue-600">({unreadCount})</span>}
          </button>
          <button
            onClick={() => setActiveTab('forum')}
            className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-t transition-colors ${
              activeTab === 'forum' 
                ? 'bg-gray-100 text-gray-900' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Forum {forumUnreadCount > 0 && <span className="ml-1 text-blue-600">({forumUnreadCount})</span>}
          </button>
        </div>
        
        {loading ? (
          <div className="px-3 py-6 text-center text-gray-500 text-sm">
            Loading...
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto">
            {/* Updates list */}
            {(activeTab === 'all' || activeTab === 'updates') && updates.length > 0 && (
              <>
                {activeTab === 'all' && updates.length > 0 && (
                  <div className="px-3 py-1 bg-gray-50 text-xs font-medium text-gray-500">System Updates</div>
                )}
                {updates.map(update => {
                  const Icon = getIcon(update.update_type);
                  const priorityColor = PRIORITY_COLORS[update.priority] || PRIORITY_COLORS.normal;
                  const unread = isUnread(update);
                  
                  return (
                    <DropdownMenuItem
                      key={update.id}
                      className={`px-3 py-2 cursor-pointer focus:bg-gray-50 ${unread ? 'bg-blue-50/50' : ''}`}
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/updates');
                      }}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className={`mt-0.5 ${priorityColor}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm text-gray-900 truncate ${unread ? 'font-semibold' : 'font-medium'}`}>
                              {update.title}
                            </p>
                            {unread && (
                              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {update.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(update.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </p>
                        </div>
                        {update.priority === 'urgent' && (
                          <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>
                        )}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}

            {/* Forum notifications list */}
            {(activeTab === 'all' || activeTab === 'forum') && forumNotifications.length > 0 && (
              <>
                {activeTab === 'all' && forumNotifications.length > 0 && (
                  <div className="px-3 py-1 bg-gray-50 text-xs font-medium text-gray-500">Forum Activity</div>
                )}
                {forumNotifications.map(notif => {
                  const Icon = FORUM_ICONS[notif.type] || MessageCircle;
                  const isForumUnread = !notif.is_read;
                  
                  return (
                    <DropdownMenuItem
                      key={notif.id}
                      className={`px-3 py-2 cursor-pointer focus:bg-gray-50 ${isForumUnread ? 'bg-green-50/50' : ''}`}
                      onClick={() => {
                        setIsOpen(false);
                        navigate(`/forum/question/${notif.reference_id}`);
                      }}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className={`mt-0.5 ${notif.type === 'forum_verified' ? 'text-green-600' : 'text-blue-600'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm text-gray-900 truncate ${isForumUnread ? 'font-semibold' : 'font-medium'}`}>
                              {getForumNotificationMessage(notif)}
                            </p>
                            {isForumUnread && (
                              <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {notif.reference_title}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notif.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}

            {/* System notifications section (payments, 2FA, live classes, etc.) */}
            {activeTab === 'all' && systemNotifications.length > 0 && (
              <>
                <div className="px-3 py-1 bg-gray-50 text-xs font-medium text-gray-500">System</div>
                {systemNotifications.map((notif) => (
                  <DropdownMenuItem
                    key={notif.id}
                    className={`px-3 py-2 cursor-pointer border-l-2 ${
                      !notif.is_read ? 'border-l-blue-500 bg-blue-50/30' : 'border-l-transparent'
                    }`}
                    onClick={async () => {
                      try { await axios.post(`${API}/notifications/${notif.id}/mark-read`); } catch (e) { /* non-blocking */ }
                      setIsOpen(false);
                      // Deep-link into the target page. `link` may be a full
                      // URL (dispatcher builds `${BACKEND_URL}/live-classes/...`)
                      // or a path — normalise to a same-origin path so
                      // react-router handles it client-side.
                      const raw = notif.link;
                      if (raw) {
                        try {
                          const u = new URL(raw, window.location.origin);
                          if (u.origin === window.location.origin) {
                            navigate(u.pathname + u.search + u.hash);
                          } else {
                            window.location.href = raw;
                          }
                        } catch (_) {
                          navigate('/updates');
                        }
                      } else {
                        navigate('/updates');
                      }
                    }}
                    data-testid={`sys-notif-${notif.id}`}
                  >
                    <div className="flex items-start gap-2 w-full">
                      <Bell className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notif.is_read ? 'font-semibold' : 'font-medium'} text-gray-900 truncate`}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{new Date(notif.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </>
            )}

            {/* Empty state */}
            {((activeTab === 'all' && allNotifications.length === 0 && systemNotifications.length === 0) ||
              (activeTab === 'updates' && updates.length === 0) ||
              (activeTab === 'forum' && forumNotifications.length === 0)) && (
              <div className="px-3 py-6 text-center text-gray-500 text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                {activeTab === 'forum' ? 'No forum notifications' : 'No updates yet'}
              </div>
            )}
          </div>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="px-3 py-2 cursor-pointer justify-center text-[#0A7EA4] font-medium"
          onClick={() => {
            setIsOpen(false);
            navigate('/updates');
          }}
        >
          View All Updates
          <ChevronRight className="w-4 h-4 ml-1" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
